import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/auth/AuthProvider';

export function AuthButton() {
  const { t } = useTranslation();
  const auth = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function signInMock() {
    setError(null);
    setIsSubmitting(true);
    try {
      await auth.signIn('dev-user', 'dev');
      setIsOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign in failed');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await auth.signIn(email, password);
      setPassword('');
      setIsOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign in failed');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (auth.isLoading) {
    return <span className="text-xs text-ink-4">Auth...</span>;
  }

  if (auth.session) {
    return (
      <button
        type="button"
        onClick={() => void auth.signOut()}
        className="text-xs font-medium px-3 py-1.5 rounded border border-border-2 text-ink-3 hover:text-accent hover:border-accent transition-colors"
      >
        Logout
      </button>
    );
  }

  if (!auth.isConfigured) {
    return (
      <button
        type="button"
        disabled
        title={t('auth.missing_config')}
        className="text-xs font-medium px-3 py-1.5 rounded border border-border text-ink-4 opacity-70 cursor-not-allowed"
      >
        Sign in
      </button>
    );
  }

  if (auth.isMock) {
    return (
      <button
        type="button"
        onClick={() => void signInMock()}
        disabled={isSubmitting}
        className="text-xs font-medium px-3 py-1.5 rounded border border-accent/40 text-accent hover:bg-accent/10 disabled:opacity-50 transition-colors"
      >
        {isSubmitting ? 'Signing in...' : 'Sign in'}
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setError(null);
          setIsOpen(!isOpen);
        }}
        className="text-xs font-medium px-3 py-1.5 rounded border border-accent/40 text-accent hover:bg-accent/10 transition-colors"
      >
        Sign in
      </button>

      {isOpen && (
        <form
          onSubmit={submit}
          className="absolute right-0 top-full z-20 mt-2 w-72 rounded border border-border bg-surface-2 p-4 shadow-xl"
        >
          <label className="block mb-3">
            <span className="block text-xs font-medium text-ink-2 mb-1">
              Email
            </span>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full bg-surface border border-border-2 rounded px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus:border-accent"
              required
            />
          </label>
          <label className="block mb-3">
            <span className="block text-xs font-medium text-ink-2 mb-1">
              Password
            </span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="w-full bg-surface border border-border-2 rounded px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus:border-accent"
              required
            />
          </label>

          {error && <div className="mb-3 text-xs text-danger">{error}</div>}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full text-xs font-medium px-3 py-2 rounded bg-accent/15 text-accent border border-accent/40 hover:bg-accent/25 disabled:opacity-50 transition-colors"
          >
            {isSubmitting ? 'Signing in...' : 'Login'}
          </button>
        </form>
      )}
    </div>
  );
}
